/*
  Staggered reveal for the header menu drawer.
  Progressive enhancement on top of Dawn's `menu-opening` class:
  no GSAP or prefers-reduced-motion means items simply appear, untouched.
*/
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var revealTweens = new WeakMap();
  var submenuCloseTimers = new WeakMap();

  var SUBMENU_PANEL_MS = 220;
  var SUBMENU_EXIT_LEAD = 72;

  var PARENT_EXIT = { duration: 0.15, stagger: 0.02, x: -10 };
  var PARENT_ENTER = { duration: 0.18, stagger: 0.028, x: -8 };
  var PANEL_ENTER = { duration: 0.22, xPercent: 12 };
  var SUBMENU_ENTER = { duration: 0.18, stagger: 0.028, x: 10, lead: 0.07 };

  function isSubmenuDetails(detailsElement) {
    return !!detailsElement.querySelector(':scope > .menu-drawer__submenu');
  }

  function isMainDrawer(detailsElement) {
    return !!detailsElement.querySelector(':scope > .menu-drawer');
  }

  function submenuItemSelector() {
    return [
      ':scope > .menu-drawer__inner-submenu > .menu-drawer__close-button',
      ':scope > .menu-drawer__inner-submenu > ul > li > .menu-drawer__menu-item',
      ':scope > .menu-drawer__close-button',
      ':scope > ul > li > .menu-drawer__menu-item',
    ].join(', ');
  }

  function nestedSubmenuItemSelector() {
    return [
      '.menu-drawer__inner-submenu > .menu-drawer__close-button',
      '.menu-drawer__inner-submenu > ul > li > .menu-drawer__menu-item',
      ':scope > .menu-drawer__close-button',
      ':scope > ul > li > .menu-drawer__menu-item',
    ].join(', ');
  }

  function itemsFor(detailsElement) {
    var panel = detailsElement.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu');
    if (!panel) return [];

    if (panel.classList.contains('menu-drawer')) {
      var items = [];
      panel.querySelectorAll('.menu-drawer__navigation > ul > li').forEach(function (el) {
        items.push(el);
      });
      panel.querySelectorAll('.menu-drawer__utility-links > *').forEach(function (el) {
        if (el.classList.contains('menu-drawer__utility-meta')) {
          el.querySelectorAll('.menu-drawer__account, .menu-drawer__instagram').forEach(function (link) {
            items.push(link);
          });
          return;
        }
        items.push(el);
      });
      return items;
    }

    return panel.querySelectorAll(submenuItemSelector());
  }

  function parentLayerExitTargets(layer, openingDetails) {
    if (!layer) return [];

    if (layer.classList.contains('menu-drawer__menu')) {
      return Array.from(layer.querySelectorAll(':scope > li'))
        .map(function (li) {
          if (openingDetails && li.contains(openingDetails)) {
            return openingDetails.querySelector(':scope > summary') || li;
          }
          return li;
        })
        .filter(Boolean);
    }

    if (layer.classList.contains('menu-drawer__submenu')) {
      var rows = [];
      var inner = layer.querySelector('.menu-drawer__inner-submenu') || layer;

      Array.from(inner.querySelectorAll(':scope > ul > li')).forEach(function (li) {
        if (openingDetails && li.contains(openingDetails)) {
          var openingSummary = openingDetails.querySelector(':scope > summary');
          if (openingSummary) rows.push(openingSummary);
          return;
        }
        rows.push(li);
      });

      var closeButton = inner.querySelector(':scope > .menu-drawer__close-button');
      if (closeButton) rows.unshift(closeButton);

      return rows;
    }

    return [];
  }

  function parentLayerRevealTargets(layer) {
    if (!layer) return [];

    if (layer.classList.contains('menu-drawer__menu')) {
      return Array.from(layer.querySelectorAll(':scope > li'));
    }

    if (layer.classList.contains('menu-drawer__submenu')) {
      var rows = [];
      var inner = layer.querySelector('.menu-drawer__inner-submenu') || layer;
      var closeButton = inner.querySelector(':scope > .menu-drawer__close-button');
      if (closeButton) rows.push(closeButton);
      inner.querySelectorAll(':scope > ul > li').forEach(function (li) {
        rows.push(li);
      });
      return rows;
    }

    return [];
  }

  function killReveal(detailsElement) {
    var tween = revealTweens.get(detailsElement);
    if (tween) {
      tween.kill();
      revealTweens.delete(detailsElement);
    }
  }

  function clearSubmenuCloseTimer(detailsElement) {
    var timer = submenuCloseTimers.get(detailsElement);
    if (timer) {
      window.clearTimeout(timer);
      submenuCloseTimers.delete(detailsElement);
    }
  }

  function utilityMetaFor(detailsElement) {
    var panel = detailsElement.querySelector(':scope > .menu-drawer');
    return panel ? panel.querySelector('.menu-drawer__utility-meta') : null;
  }

  function utilityLineFor(detailsElement) {
    var meta = utilityMetaFor(detailsElement);
    return meta ? meta.querySelector('.menu-drawer__utility-meta__line') : null;
  }

  function drawUtilityLine(detailsElement, itemCount) {
    var line = utilityLineFor(detailsElement);
    if (!line || !window.gsap) return;

    window.gsap.killTweensOf(line);
    line.classList.remove('is-drawn');
    window.gsap.set(line, { scaleX: 0, transformOrigin: 'left center' });

    if (reducedMotion.matches) {
      window.gsap.set(line, { scaleX: 1, clearProps: 'transform' });
      line.classList.add('is-drawn');
      return;
    }

    window.gsap.to(line, {
      scaleX: 1,
      duration: 0.32,
      ease: 'power3.out',
      delay: 0.08 + Math.max(itemCount - 1, 0) * 0.035,
      onComplete: function () {
        line.classList.add('is-drawn');
      },
    });
  }

  function resetUtilityLine(detailsElement) {
    var line = utilityLineFor(detailsElement);
    if (!line) return;

    if (window.gsap) window.gsap.killTweensOf(line);
    line.classList.remove('is-drawn');
    if (window.gsap) {
      window.gsap.set(line, { scaleX: 0, transformOrigin: 'left center' });
    }
  }

  function resetSubmenuPanel(panel) {
    if (!panel || !window.gsap) return;
    window.gsap.killTweensOf(panel);
    panel.classList.remove('submenu-panel-ready');
    window.gsap.set(panel, { clearProps: 'transform,opacity' });
  }

  function animateSubmenuEnterSequence(detailsElement, layer, items) {
    killReveal(detailsElement);

    var panel = detailsElement.querySelector(':scope > .menu-drawer__submenu');
    if (!panel || !window.gsap) return;

    var parentItems = parentLayerExitTargets(layer, detailsElement);

    window.gsap.killTweensOf([panel].concat(parentItems, Array.from(items)));
    panel.classList.remove('submenu-closing');
    panel.classList.remove('submenu-panel-ready');
    window.gsap.set(panel, { xPercent: PANEL_ENTER.xPercent, opacity: 0 });
    window.gsap.set(items, { autoAlpha: 0, x: SUBMENU_ENTER.x });

    var timeline = window.gsap.timeline({
      onComplete: function () {
        panel.classList.add('submenu-panel-ready');
        window.gsap.set(panel, { clearProps: 'transform,opacity' });
      },
    });

    revealTweens.set(detailsElement, timeline);

    timeline.to(parentItems, {
      autoAlpha: 0,
      x: PARENT_EXIT.x,
      duration: PARENT_EXIT.duration,
      stagger: { each: PARENT_EXIT.stagger, from: 'end' },
      ease: 'power2.out',
      onComplete: function () {
        window.gsap.set(parentItems, { pointerEvents: 'none' });
      },
    });

    timeline.to(
      panel,
      {
        xPercent: 0,
        opacity: 1,
        duration: PANEL_ENTER.duration,
        ease: 'power3.out',
      },
      '>'
    );

    timeline.to(
      items,
      {
        autoAlpha: 1,
        x: 0,
        duration: SUBMENU_ENTER.duration,
        stagger: SUBMENU_ENTER.stagger,
        ease: 'power3.out',
        clearProps: 'all',
      },
      '<+=0.07'
    );
  }

  function revealParentLayer(layer) {
    if (!layer || reducedMotion.matches || !window.gsap) return;

    var items = parentLayerRevealTargets(layer);
    if (!items.length) return;

    window.gsap.killTweensOf(items);
    window.gsap.fromTo(
      items,
      { autoAlpha: 0, x: PARENT_ENTER.x },
      {
        autoAlpha: 1,
        x: 0,
        duration: PARENT_ENTER.duration,
        ease: 'power3.out',
        stagger: PARENT_ENTER.stagger,
        clearProps: 'all',
      }
    );
  }

  function reveal(detailsElement) {
    var items = itemsFor(detailsElement);
    if (!items.length) return;

    clearSubmenuCloseTimer(detailsElement);

    var submenuPanel = detailsElement.querySelector(':scope > .menu-drawer__submenu');
    if (submenuPanel) submenuPanel.classList.remove('submenu-closing');

    if (isMainDrawer(detailsElement)) {
      drawUtilityLine(detailsElement, items.length);
    }

    if (isSubmenuDetails(detailsElement)) {
      if (reducedMotion.matches || !window.gsap) return;

      animateSubmenuEnterSequence(detailsElement, detailsElement.closest('.has-submenu'), items);
      return;
    }

    if (reducedMotion.matches || !window.gsap) return;

    killReveal(detailsElement);
    revealTweens.set(
      detailsElement,
      window.gsap.fromTo(
        items,
        { autoAlpha: 0, y: 10 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.26,
          ease: 'power3.out',
          stagger: 0.035,
          delay: 0.06,
          clearProps: 'all',
        }
      )
    );
  }

  function reset(detailsElement) {
    if (isSubmenuDetails(detailsElement)) {
      var panel = detailsElement.querySelector(':scope > .menu-drawer__submenu');
      if (panel && panel.classList.contains('submenu-closing')) return;
      if (detailsElement.hasAttribute('open')) return;
    }

    var items = itemsFor(detailsElement);
    if (!items.length) return;

    killReveal(detailsElement);

    if (window.gsap) {
      window.gsap.killTweensOf(items);
      window.gsap.set(items, { clearProps: 'all' });
    }

    if (isSubmenuDetails(detailsElement)) {
      resetSubmenuPanel(detailsElement.querySelector(':scope > .menu-drawer__submenu'));
    }

    if (isMainDrawer(detailsElement)) {
      resetUtilityLine(detailsElement);
    }
  }

  function animateSubmenuExit(detailsElement, onPanelExit) {
    var items = itemsFor(detailsElement);

    killReveal(detailsElement);

    if (window.gsap && items.length && !reducedMotion.matches) {
      window.gsap.to(items, {
        autoAlpha: 0,
        x: PARENT_EXIT.x,
        duration: 0.12,
        stagger: { each: 0.012, from: 'end' },
        ease: 'power2.out',
      });
    }

    window.setTimeout(onPanelExit, SUBMENU_EXIT_LEAD);
  }

  function patchSubmenuClose() {
    var Drawer = customElements.get('menu-drawer');
    if (!Drawer || Drawer.prototype._lumanoSubmenuClosePatched) return;

    var originalCloseSubmenu = Drawer.prototype.closeSubmenu;

    Drawer.prototype.closeSubmenu = function (detailsElement) {
      var submenuPanel = detailsElement.querySelector(':scope > .menu-drawer__submenu');
      var parentMenuElement = detailsElement.closest('.has-submenu');

      if (!submenuPanel || !parentMenuElement || reducedMotion.matches) {
        return originalCloseSubmenu.call(this, detailsElement);
      }

      clearSubmenuCloseTimer(detailsElement);

      animateSubmenuExit(detailsElement, function () {
        submenuPanel.classList.add('submenu-closing');
        submenuPanel.classList.remove('submenu-panel-ready');
        detailsElement.classList.remove('menu-opening');
        detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
        removeTrapFocus(detailsElement.querySelector('summary'));
        this.closeAnimation(detailsElement);

        var finished = false;

        function finish() {
          if (finished) return;
          finished = true;
          clearSubmenuCloseTimer(detailsElement);
          submenuPanel.removeEventListener('transitionend', onTransitionEnd);
          submenuPanel.classList.remove('submenu-closing');
          parentMenuElement.classList.remove('submenu-open');
          if (window.gsap) {
            window.gsap.set(itemsFor(detailsElement), { clearProps: 'all' });
            window.gsap.set(detailsElement.querySelector(':scope > summary'), { clearProps: 'all' });
            window.gsap.set(parentLayerRevealTargets(parentMenuElement), { clearProps: 'all' });
            resetSubmenuPanel(submenuPanel);
          }
          revealParentLayer(parentMenuElement);

          var openParent = detailsElement.closest('details[open]');
          if (openParent) {
            trapFocus(openParent, detailsElement.querySelector('summary'));
          }
        }

        function onTransitionEnd(event) {
          if (event.target !== submenuPanel) return;
          if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
          finish();
        }

        submenuPanel.addEventListener('transitionend', onTransitionEnd);
        submenuCloseTimers.set(
          detailsElement,
          window.setTimeout(finish, SUBMENU_EXIT_LEAD + SUBMENU_PANEL_MS + 32)
        );
      }.bind(this));
    };

    Drawer.prototype._lumanoSubmenuClosePatched = true;
  }

  function init() {
    patchSubmenuClose();
    if (!window.gsap) return;

    document.documentElement.classList.add('lumano-menu-gsap');

    var drawers = document.querySelectorAll('header-drawer');
    if (!drawers.length) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.target.tagName !== 'DETAILS') return;

        var wasOpening = (mutation.oldValue || '').indexOf('menu-opening') !== -1;
        var isOpening = mutation.target.classList.contains('menu-opening');

        if (isOpening && !wasOpening) {
          reveal(mutation.target);
        } else if (!isOpening && wasOpening) {
          reset(mutation.target);
        }
      });
    });

    drawers.forEach(function (drawer) {
      observer.observe(drawer, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
